import {
  Component, OnInit, AfterViewInit, OnDestroy,
  ViewChild, ElementRef, ChangeDetectorRef
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppService } from '../app.service';
import { messageService } from '../message.service';
import { SuccessSnackbarComponent } from '../support/success-snackbar/success-snackbar.component';

@Component({
  selector: 'app-esignature-sign',
  templateUrl: './esignature-sign.component.html',
  styleUrls: ['./esignature-sign.component.scss']
})
export class EsignatureSignComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('pdfCanvas') pdfCanvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('overlayCanvas') overlayCanvas: ElementRef<HTMLCanvasElement>;

  isProcessing = false;
  envelopeId: number;
  signerId: number;
  envelopeData: any = {};
  mySignerRecord: any = null;
  myField: any = null;

  pdfJsLib: any;
  pdfDoc: any = null;
  currentPage = 1;
  totalPages = 0;
  pdfScale = 1.2;

  // All signer records for the current user/layer (one per field)
  myAllSignerRecords: any[] = [];

  // Unified sign form — one entry per non-date field for this user
  signFieldInputs: { signerNo: number; fieldId: string; type: string; label: string; value: string; signFont: string }[] = [];

  selectedFont = 'Dancing Script';
  signFonts = [
    { value: 'Dancing Script', label: 'Cursive' },
    { value: 'Great Vibes', label: 'Elegant' },
    { value: 'Pacifico', label: 'Casual' },
    { value: 'Sacramento', label: 'Formal' }
  ];
  showSignModal = false;
  declineComments = '';
  showDeclineModal = false;


  // Final document state
  finalDocUrl: string | null = null;
  isFinalizingDoc = false;
  finalizeError = false;

  // Sign button enabled only when ALL non-date fields have values
  get canSubmitSign(): boolean {
    return this.signFieldInputs.length > 0 &&
      this.signFieldInputs.every(f => f.value?.trim().length > 0);
  }

  // Preview text for the signature font selector (uses first signature field value)
  get signaturePreviewText(): string {
    const sigField = this.signFieldInputs.find(f => f.type === 'signature');
    return sigField?.value?.trim() || 'Your Signature';
  }

  uiDateFormat = 'dd-MMM-yyyy';

  // ── FIX: signers VW STATUS is 'OPEN' (not 'Pending').
  // isPendingStatus() accepts both to be safe.
  isPendingStatus(status: string): boolean {
    return status === 'OPEN' || status === 'Pending';
  }

  // Signed = ALL signer records for this user/layer have signedOn populated.
  // This includes signature, initials, text — once all are done, status flips to Signed.
  get hasAlreadySigned(): boolean {
    if (this.myAllSignerRecords.length > 0) {
      return this.myAllSignerRecords.every((s: any) => s.signedOn != null);
    }
    return this.mySignerRecord?.signedOn != null && !!this.mySignerRecord?.signedName;
  }

  get isMyTurn(): boolean {
    // Not my turn if I've already signed (signedOn populated) regardless of VW status
    if (this.hasAlreadySigned) return false;
    return this.mySignerRecord &&
      this.isPendingStatus(this.mySignerRecord.status) &&
      Number(this.mySignerRecord.layerNo) === Number(this.envelopeData.currentLayer);
  }

  constructor(
    private appService: AppService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    public message: messageService,
    private cdr: ChangeDetectorRef
  ) {
    const nav = this.router.getCurrentNavigation();
    if (nav?.extras?.state) {
      this.envelopeId = nav.extras.state['envelopeId'];
      this.signerId = nav.extras.state['signerId'];
    } else {
      this.envelopeId = history.state.envelopeId;
      this.signerId = history.state.signerId;
    }
  }

  ngOnInit(): void {
    if (this.envelopeId) {
      this.loadEnvelope();
    } else {
      this.showSnackBar('Invalid session. Please open the task from My Tasks.', 'E');
      this.router.navigate(['/dashboard/home']);
    }

    const tzObj = JSON.parse(sessionStorage.getItem('getUserTimezoneDetails') || '{}');
    if (tzObj.uiDateFormat) {
      this.uiDateFormat = tzObj.uiDateFormat
        .replace('DD', 'dd').replace('MON', 'MMM').replace('YYYY', 'yyyy');
    }
  }

  ngAfterViewInit(): void { }

  // Auto-submit DATE-type fields for the signed user/layer.
  // The sign modal only handles the primary field. Date fields are auto-filled
  // with the current date when the user completes their signature.
  private async _autoFillDateFields(layerNo: number, userId: number): Promise<void> {
    const fields: any[] = JSON.parse(this.envelopeData.fieldMetadata || '[]');
    const dateFields = fields.filter(
      (f: any) => f.type === 'date' && f.layerNo === layerNo
        && Number(f.assignedUserId) === userId
    );
    if (!dateFields.length) return;

    const now = new Date();
    const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

    // Find the signer record for date fields (may reuse same signerNo with different fieldId)
    const signerNo = this.mySignerRecord?.signerNo;

    const calls = dateFields.map((f: any) =>
      new Promise<void>(resolve => {
        const datePay = {
          envelopeNo: this.envelopeId,
          signerNo: signerNo,
          layerNo,
          fieldId: f.fieldId,
          orgId: +this.message.getOrgId(),
          userId,
          signedName: dateStr,
          signFont: 'Arial',
          action: 'Sign',
          comments: '',
          ipAddress: ''
        };
        this.appService.performEsignAction(datePay).subscribe({
          next: () => resolve(),
          error: () => resolve()  // don't block main sign on date field failure
        });
      })
    );
    await Promise.all(calls);
  }


  // Auto-submit DATE-type signer records for the signed user/layer.
  // Each date field has its own signerNo (one-signer-per-field model).
  // signedName stores the formatted date/time string.
  // Auto-submit DATE and INITIALS signer records.
  // DATE fields → current timestamp; INITIALS fields → derived from typed name.
  // TEXT fields are submitted separately via the sign modal user input.
  // Build Promise array for DATE-type fields (auto-stamped with current date/time)
  private _buildDateSignCalls(layerNo: number, userId: number, orgId: number): Promise<void>[] {
    const allFields: any[] = JSON.parse(this.envelopeData.fieldMetadata || '[]');
    const signers: any[] = this.envelopeData.signers || [];
    const now = new Date();
    const dp = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const tp = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const dateTimeStr = `${dp} ${tp}`;

    return signers
      .filter(s => Number(s.userId) === userId && Number(s.layerNo) === layerNo && s.signedOn == null)
      .filter(s => { const fld = allFields.find((f: any) => f.fieldId === s.fieldId); return fld?.type === 'date'; })
      .map(s => new Promise<void>(resolve => {
        this.appService.performEsignAction({
          envelopeNo: this.envelopeId, signerNo: s.signerNo, layerNo, fieldId: s.fieldId,
          orgId, userId, signedName: dateTimeStr, signFont: 'Arial',
          action: 'Sign', comments: '', ipAddress: ''
        }).subscribe({ next: () => resolve(), error: () => resolve() });
      }));
  }





  // ── Layer status helpers ────────────────────────────────────────────────

  // Unique sorted layer numbers across all signers
  getLayerNos(): number[] {
    const layers = new Set<number>(
      (this.envelopeData.signers || []).map((s: any) => Number(s.layerNo))
    );
    return Array.from(layers).sort((a, b) => a - b);
  }

  getSignersForLayer(layerNo: number): any[] {
    return (this.envelopeData.signers || [])
      .filter((s: any) => Number(s.layerNo) === layerNo);
  }

  // Derive layer status from signedOn (VW status stays "OPEN" after signing)
  getLayerStatus(layerNo: number): { label: string; css: string } {
    const layerSigners = this.getSignersForLayer(layerNo);
    if (!layerSigners.length) return { label: 'Pending', css: 'pending' };

    const allSigned = layerSigners.every((s: any) => s.signedOn != null && s.signedName);
    const totalLayers = Number(this.envelopeData.totalLayers) || 1;
    const currentLayer = Number(this.envelopeData.currentLayer) || 1;
    const isLastLayer = layerNo >= totalLayers;

    if (allSigned) {
      return isLastLayer
        ? { label: 'Document Signed', css: 'document-signed' }
        : { label: `Layer ${layerNo} Signed`, css: 'signed' };
    }
    if (layerNo > currentLayer) return { label: 'Waiting', css: 'waiting' };
    return { label: 'Pending', css: 'pending' };
  }

  isSignerSigned(signer: any): boolean {
    return signer?.signedOn != null && !!signer?.signedName;
  }


  // True when every signer in a given layer has signedOn populated
  isLayerSigned(layerNo: number): boolean {
    const layerSigners = (this.envelopeData?.signers || [])
      .filter((s: any) => Number(s.layerNo) === Number(layerNo));
    return layerSigners.length > 0 && layerSigners.every((s: any) => s.signedOn != null);
  }

  // True when every signer across all layers has signed
  isDocumentSigned(): boolean {
    const signers: any[] = this.envelopeData?.signers || [];
    return signers.length > 0 && signers.every((s: any) => s.signedOn != null);
  }

  // Status label for a signer row: "Document Signed" → "Layer X Signed" → "Pending"
  getSignerStatusLabel(signer: any): string {
    if (signer.signedOn != null && signer.signedName) return 'Signed';
    if (this.isDocumentSigned()) return 'Document Signed';
    if (this.isLayerSigned(signer.layerNo)) return 'Layer ' + signer.layerNo + ' Signed';
    return 'Pending';
  }


  // Called automatically when all layers are signed.
  // Sends the current PDF bytes + envelopeNo to the backend for iText flattening.
  // Result is saved to FINAL_DOCUMENT BLOB in the envelope table.
  finalizeDocument(): void {
    if (this.isFinalizingDoc || this.finalDocUrl) return;
    this.isFinalizingDoc = true;
    this.finalizeError = false;
    this.cdr.detectChanges();

    // Backend reads ORIGINAL_PDF directly from the envelope table (stored at send time).
    // No PDF bytes ever transferred from browser — zero ArrayBuffer/SharedArrayBuffer risk.
    this.appService.finalizeEsignEnvelope(this.envelopeId).subscribe({
      next: (res: any) => {
        this.isFinalizingDoc = false;
        if (res?.resultType === 'S') {
          this.finalDocUrl = this.appService.getEsignFinalDocumentUrl(this.envelopeId);
        } else {
          this.finalizeError = true;
        }
        this.cdr.detectChanges();
      },
      error: () => { this.isFinalizingDoc = false; this.finalizeError = true; this.cdr.detectChanges(); }
    });
  }


  downloadFinalDoc(): void {
    if (!this.finalDocUrl) return;
    this.appService.getFinalDocumentBlob(this.envelopeId).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Filename: Signed_[DocumentName]_[DD-Mon-YYYY].pdf
        const rawTitle = (this.envelopeData?.envelopeTitle || 'document');
        const docName = rawTitle
          .replace(/\s*[-–]?\s*e[-\s]?sign(ature)?\s*$/i, '') // strip "- E-Signature"
          .replace(/[^a-zA-Z0-9\-]/g, '_')
          .replace(/_+/g, '_').replace(/^_|_$/g, '') || 'document';
        const dateStr = new Date().toLocaleDateString('en-GB', {
          day: '2-digit', month: 'short', year: 'numeric'
        }).replace(/ /g, '-');
        a.download = `Signed_${docName}_${dateStr}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      error: () => this.showSnackBar('Download failed. Please retry.', 'E')
    });
  }

  // Get the field type for a signer record (for right panel display)
  getFieldTypeForSigner(signer: any): string {
    if (!signer?.fieldId) return 'signature';
    try {
      const fields: any[] = JSON.parse(this.envelopeData?.fieldMetadata || '[]');
      return fields.find((f: any) => f.fieldId === signer.fieldId)?.type || 'signature';
    } catch { return 'signature'; }
  }

  // Get display name from fieldMetadata assignedUserName by userId.
  // Signer records only have userId; names come from fieldMetadata.
  getUserName(userId: number): string {
    try {
      const fields: any[] = JSON.parse(this.envelopeData?.fieldMetadata || '[]');
      const field = fields.find((f: any) => Number(f.assignedUserId) === Number(userId));
      return field?.assignedUserName || `User ${userId}`;
    } catch { return `User ${userId}`; }
  }

  ngOnDestroy(): void { }

  loadEnvelope() {
    this.isProcessing = true;
    this.appService.getEsignEnvelope(this.envelopeId).subscribe({
      next: (data: any) => {
        if (data?.resultType === 'E') {
          this.showSnackBar(data.result || 'Failed to load envelope.', 'E');
          this.isProcessing = false;
          return;
        }
        this.envelopeData = data;

        // FIX: one signer record per field. Find by signerId (from My Tasks nav state)
        // for the primary signing record. Date-type field signers are handled in drawFields.
        const meUserId = this.message.getUserId();
        const allFields: any[] = JSON.parse(data.fieldMetadata || '[]');

        if (this.signerId) {
          this.mySignerRecord = data.signers?.find(
            (s: any) => Number(s.signerNo) === Number(this.signerId)
          );
        } else {
          this.mySignerRecord = data.signers?.find((s: any) => {
            if (Number(s.userId) !== meUserId || !this.isPendingStatus(s.status)) return false;
            const fld = allFields.find((f: any) => f.fieldId === s.fieldId);
            return fld ? fld.type !== 'date' : true;
          });
        }

        // Collect ALL signer records for this user on currentLayer
        const currentLayerNo = Number(data.currentLayer || 1);
        this.myAllSignerRecords = (data.signers || []).filter(
          (s: any) => Number(s.userId) === meUserId && Number(s.layerNo) === currentLayerNo
        );

        if (this.mySignerRecord) {
          this.myField = allFields.find((f: any) => f.fieldId === this.mySignerRecord.fieldId);
        }

        this.isProcessing = false;
        this.cdr.detectChanges();
        this.loadPdfJs();

        // Auto-trigger finalize when every signer has signedOn.
        // Set isFinalizingDoc = true immediately to block any concurrent call
        // (e.g. user clicking "Generate & Download" while auto-trigger is pending).
        // Auto-trigger finalize when every signer has signedOn.
        // NOTE: do NOT set isFinalizingDoc = true here — finalizeDocument() sets it
        // itself. Setting it here would cause finalizeDocument() to bail immediately
        // on the guard check: if (this.isFinalizingDoc ...) return;
        if (this.isDocumentSigned() && !this.finalDocUrl && !this.isFinalizingDoc) {
          setTimeout(() => this.finalizeDocument(), 1200);
        }
      },
      error: () => {
        this.isProcessing = false;
        this.showSnackBar('Could not load signing request.', 'E');
      }
    });
  }

  loadPdfJs() {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap';
    document.head.appendChild(link);

    if ((window as any)['pdfjsLib']) {
      this.pdfJsLib = (window as any)['pdfjsLib'];
      this.pdfJsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      this.loadDocumentPdf();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.onload = () => {
      this.pdfJsLib = (window as any)['pdfjsLib'];
      this.pdfJsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      this.loadDocumentPdf();
    };
    document.head.appendChild(script);
  }

  loadDocumentPdf() {
    this.isProcessing = true;
    if (!this.envelopeData?.documentId || !this.envelopeData?.version) {
      this.showSnackBar('Could not load document: missing document details.', 'E');
      this.isProcessing = false;
      return;
    }

    // FIX: was getDocumentsToViewForDms → returns raw DOCX, PDF viewer can't render.
    // getDocumentAsPdfForEsign converts the document to PDF before returning.
    this.appService.getDocumentAsPdfForEsign(

      this.envelopeData.documentId,
      this.envelopeData.version
    ).subscribe({
      next: (response: any) => {
        const uint8 = new Uint8Array(response.body);
        // Pass uint8 directly to PDF.js for rendering.
        // Original PDF is stored server-side (ORIGINAL_PDF BLOB) — no local copy needed.
        this.pdfJsLib.getDocument({ data: uint8 }).promise.then((doc: any) => {
          this.pdfDoc = doc;
          this.totalPages = doc.numPages;
          this.isProcessing = false;
          this.cdr.detectChanges();
          const startPage = this.myField?.pageNo || 1;
          // Pre-load all signature fonts before rendering so drawFields() can draw them
          // immediately. Without this, custom fonts render in fallback and Canvas
          // document.fonts.load() inside drawFields() causes an infinite render loop.
          const fontSpecs = [
            "italic 20px 'Sacramento'",
            "italic 20px 'Dancing Script'",
            "italic 20px 'Great Vibes'",
            "italic 20px 'Pacifico'"
          ];
          Promise.all(fontSpecs.map(f => document.fonts.load(f).catch(() => { })))
            .finally(() => this.renderPage(startPage));
        }).catch(() => {
          this.isProcessing = false;
          this.showSnackBar('Document could not be rendered as PDF.', 'E');
        });
      },
      error: () => {
        this.isProcessing = false;
        this.showSnackBar('Could not load document.', 'E');
      }
    });
  }

  renderPage(pageNum: number) {
    if (!this.pdfDoc || !this.pdfCanvas) return;
    this.currentPage = pageNum;
    this.pdfDoc.getPage(pageNum).then((page: any) => {
      const viewport = page.getViewport({ scale: this.pdfScale });
      const canvas = this.pdfCanvas.nativeElement;
      const ctx = canvas.getContext('2d')!;
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const overlay = this.overlayCanvas.nativeElement;
      overlay.height = viewport.height;
      overlay.width = viewport.width;

      page.render({ canvasContext: ctx, viewport }).promise.then(() => {
        this.drawFields(overlay);
      });
    });
  }

  drawFields(overlay: HTMLCanvasElement) {
    const ctx = overlay.getContext('2d')!;
    ctx.clearRect(0, 0, overlay.width, overlay.height);

    const fields = JSON.parse(this.envelopeData.fieldMetadata || '[]');
    const signers = this.envelopeData.signers || [];

    for (const field of fields) {
      if (field.pageNo !== this.currentPage) continue;

      const x = (field.x / 100) * overlay.width;
      const y = (field.y / 100) * overlay.height;
      const w = (field.w / 100) * overlay.width;
      const h = (field.h / 100) * overlay.height;

      // ── REDACT: always solid black, no signer needed ────────────────────
      if (field.type === 'redact') {
        ctx.fillStyle = '#000000';
        ctx.fillRect(x, y, w, h);
        ctx.restore?.();
        continue;
      }

      // Primary lookup: by fieldId (new envelopes, one signer per field)
      // Fallback:        by userId+layerNo (old envelopes, fieldId=null)
      let signer = signers.find((s: any) => s.fieldId === field.fieldId);
      if (!signer && field.assignedUserId) {
        signer = signers.find((s: any) =>
          Number(s.userId) === Number(field.assignedUserId) &&
          Number(s.layerNo) === Number(field.layerNo) &&
          s.signedOn != null);
      }
      // Highlight ALL of the user's pending fields (not just primary)
      const isMyField = this.isMyTurn && (
        (this.myField && field.fieldId === this.myField.fieldId) ||
        this.myAllSignerRecords.some((s: any) => s.fieldId === field.fieldId && s.signedOn == null)
      );

      // FIX: VW status stays 'OPEN' after signing. Use signedOn as indicator.
      const isSigned = (signer?.signedOn != null && signer?.signedName) || signer?.status === 'Signed';
      if (isSigned) {
        // FIX: scale font so signature fits within field bounds — never overflow
        const maxW = w - 8;
        const maxH = h - 8;
        let fontSize = Math.min(24, maxH * 0.7);
        ctx.font = `italic ${fontSize}px '${signer.signFont || 'Dancing Script'}'`;
        while (fontSize > 7 && ctx.measureText(signer.signedName).width > maxW) {
          fontSize -= 0.5;
          ctx.font = `italic ${fontSize}px '${signer.signFont || 'Dancing Script'}'`;
        }

        let dateStr = '';
        if (signer?.signedOn) {
          const d = new Date(signer.signedOn);
          if (!isNaN(d.getTime())) {
            const dp = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            const tp = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
            dateStr = `${dp} | ${tp}`;
          }
        }

        // 1. DATE FIELD
        if (field.type === 'date') {
          ctx.font = `bold 10px Arial`;
          ctx.fillStyle = '#155724';
          ctx.fillText(dateStr || '—', x + 4, y + h / 2 + 4);

          // 2. TEXT FIELD
        } else if (field.type === 'text') {
          ctx.font = `bold ${Math.min(11, h * 0.4)}px Arial`;
          ctx.fillStyle = '#212529';
          ctx.fillText(signer.signedName, x + 4, y + h / 2 + 4);

          // 3. INITIALS FIELD (Just Cursive)
        } else if (field.type === 'initials') {
          ctx.fillStyle = '#1a237e';
          ctx.fillText(signer.signedName, x + 4, y + h / 2 + fontSize / 3);

          // 4. SIGNATURE FIELD (OneQms Signature Seal)
        } else {
          // If the box is extremely small, fallback to just cursive.
          // Otherwise, draw the full OneQms stamp.
          if (h > 45 && w > 120) {
            let currentY = y + 12;

            // Header
            ctx.font = 'bold 9px Arial';
            ctx.fillStyle = '#495057';
            ctx.fillText('OneQms Signature Seal:', x + 4, currentY);
            currentY += fontSize + 2;

            // Cursive Signature
            ctx.font = `italic ${fontSize}px '${signer.signFont || 'Dancing Script'}'`;
            ctx.fillStyle = '#0056b3'; // Deep Blue for the signature
            ctx.fillText(signer.signedName, x + 16, currentY);
            currentY += 12;

            // Metadata Lines
            ctx.font = '9px Arial';
            ctx.fillStyle = '#212529';

            ctx.fillText(`Signer Name: ${signer.signedName}`, x + 4, currentY);
            currentY += 11;

            const reason = signer.comments || 'I am the author of this document';
            ctx.fillText(`Reason: ${reason}`, x + 4, currentY);
            currentY += 11;

            ctx.fillText(`Time: ${dateStr}`, x + 4, currentY);
            currentY += 11;

            // Render a truncated Hash if it exists
            if (signer.signatureHash) {
              const shortHash = signer.signatureHash.substring(0, 16) + '...';
              ctx.fillText(`Hash: ${shortHash}`, x + 4, currentY);
            }
          } else {
            // Box too small for the stamp — render standard cursive
            ctx.fillStyle = '#0056b3';
            ctx.fillText(signer.signedName, x + 4, y + h / 2 + fontSize / 3);
          }
        }
      } else {
        ctx.fillStyle = (isMyField && this.isMyTurn) ? 'rgba(0,86,179,0.12)' : 'rgba(200,200,200,0.1)';
        ctx.strokeStyle = (isMyField && this.isMyTurn) ? '#0056b3' : '#ccc';
        ctx.lineWidth = (isMyField && this.isMyTurn) ? 2 : 1;
        ctx.setLineDash((isMyField && this.isMyTurn) ? [6, 3] : [3, 3]);
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, 4);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = (isMyField && this.isMyTurn) ? '#0056b3' : '#999';
        ctx.font = (isMyField && this.isMyTurn) ? 'bold 11px Arial' : '10px Arial';
        let displayText = field.label || field.type;
        if (isMyField) {
          if (field.type === 'date') {
            displayText = '📅 Auto';
          } else if (field.type === 'initials') {
            displayText = '▶ Initials (auto)';
          } else if (field.type === 'text') {
            displayText = '▶ Click to type';
          } else {
            displayText = '▶ Click to sign here';
          }
        }
        ctx.fillText(displayText, x + 6, y + h / 2 + 4);
      }
    }
  }

  onOverlayClick(event: MouseEvent) {
    if (!this.isMyTurn || !this.mySignerRecord) return;

    const overlay = this.overlayCanvas.nativeElement;
    const rect = overlay.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / overlay.width) * 100;
    const py = ((event.clientY - rect.top) / overlay.height) * 100;

    // Allow clicking on ANY of the user's pending fields (not just the primary one)
    const allFields: any[] = JSON.parse(this.envelopeData?.fieldMetadata || '[]');
    const hitAnyMyField = allFields.some(field => {
      if (field.type === 'date') return false;  // date fields are auto-filled, skip click
      const isPendingMyField = this.myAllSignerRecords.some(
        (s: any) => s.fieldId === field.fieldId && s.signedOn == null
      );
      if (!isPendingMyField) return false;
      return px >= (field.x - 2) && px <= (field.x + field.w + 2) &&
        py >= (field.y - 2) && py <= (field.y + field.h + 2);
    });

    if (hitAnyMyField) this.openSignModal();
  }

  openSignModal() {
    if (this.hasAlreadySigned || !this.isMyTurn) return;
    const allFields: any[] = JSON.parse(this.envelopeData?.fieldMetadata || '[]');
    const loggedName = localStorage.getItem('loggedUserName') || '';
    const meUserId = this.message.getUserId();
    const layerNo = Number(this.mySignerRecord?.layerNo || 1);

    // Strategy 1: one-signer-per-field (new envelopes — fieldId populated in signer)
    const fromSigners = this.myAllSignerRecords
      .filter(s => s.signedOn == null && s.fieldId)
      .map(s => {
        const fld = allFields.find((f: any) => f.fieldId === s.fieldId);
        if (!fld || fld.type === 'date' || fld.type === 'redact') return null;
        return {
          signerNo: s.signerNo,
          fieldId: s.fieldId,
          type: fld.type,
          label: fld.type === 'signature' ? 'Full Name'
            : fld.type === 'initials' ? 'Your Initials'
              : (fld.label || 'Text'),
          value: (fld.type === 'signature' || fld.type === 'initials') ? loggedName : '',
          signFont: this.selectedFont
        };
      })
      .filter(Boolean) as any[];

    if (fromSigners.length > 0) {
      this.signFieldInputs = fromSigners;
    } else {
      // Strategy 2: fallback for old envelopes (fieldId=null in signers).
      // Build inputs from fieldMetadata: match by assignedUserId + layerNo.
      this.signFieldInputs = allFields
        .filter((f: any) => Number(f.assignedUserId) === meUserId
          && Number(f.layerNo) === layerNo
          && f.type !== 'date'
          && f.type !== 'redact')
        .map((f: any) => ({
          signerNo: this.mySignerRecord?.signerNo,  // single record — old model
          fieldId: f.fieldId,                       // from metadata (correct position)
          type: f.type,
          label: f.type === 'signature' ? 'Full Name'
            : f.type === 'initials' ? 'Your Initials'
              : (f.label || 'Text'),
          value: (f.type === 'signature' || f.type === 'initials') ? loggedName : '',
          signFont: this.selectedFont
        }));
    }

    this.showSignModal = true;
  }

  // signaturePreviewText is defined as a getter in the properties section above

  submitSignature() {
    if (!this.canSubmitSign) return;
    this.isProcessing = true;
    this.showSignModal = false;

    const layerNo = Number(this.mySignerRecord.layerNo);
    const userId = parseInt(this.message.getUserId().toString()!);
    const orgId = parseInt(this.message.getOrgId().toString()!);
    const tzObj = JSON.parse(sessionStorage.getItem('getUserTimezoneDetails') ?? '{}');
    const userTimeZone = tzObj.timeZone || 'Asia/Kolkata';
    const browserUserAgent = navigator.userAgent; // Capture browser fingerprint

    const primarySigner = this.signFieldInputs.length > 0
      ? this.signFieldInputs[0].signerNo : null;

    const fieldCalls = this.signFieldInputs.map((f, idx) => {
      const isFallbackDuplicate = idx > 0 && f.signerNo === primarySigner;
      const effectiveSignerNo = isFallbackDuplicate ? null : f.signerNo;

      return new Promise<void>(resolve => {
        this.appService.performEsignAction({
          envelopeNo: this.envelopeId,
          signerNo: effectiveSignerNo,
          layerNo,
          fieldId: f.fieldId,
          orgId,
          userId,
          signedName: f.value.trim(),
          signFont: (f.type === 'text') ? 'Arial' : (f.signFont || this.selectedFont),
          action: 'Sign',
          comments: f.type === 'signature' ? 'I am the author of this document' : '',
          ipAddress: '', // Left blank; captured securely backend-side via HttpServletRequest
          userAgent: browserUserAgent, // ◄ PASS FINGERPRINT
          timeZone: userTimeZone    // ◄ PASS TIMEZONE
        }).subscribe({ next: () => resolve(), error: () => resolve() });
      });
    });

    // Also auto-submit DATE fields
    const dateCalls = this._buildDateSignCalls(layerNo, userId, orgId);

    Promise.all([...fieldCalls, ...dateCalls]).then(() => {
      this.isProcessing = false;
      this.showSnackBar('Document signed successfully!', 'S');
      // Reload — if this was the last signer, loadEnvelope will detect isDocumentSigned()
      // and auto-trigger finalizeDocument() which saves to DB + sets download URL
      setTimeout(() => this.loadEnvelope(), 800);
    }).catch(() => {
      this.isProcessing = false;
      this.showSnackBar('Sign action failed. Please retry.', 'E');
    });
  }

  openDeclineModal() { this.showDeclineModal = true; }

  submitDecline() {
    if (!this.declineComments.trim()) {
      this.showSnackBar('Please provide a reason for declining.', 'E');
      return;
    }
    this.isProcessing = true;
    this.showDeclineModal = false;

    const payload = {
      envelopeNo: this.envelopeId,
      signerNo: this.mySignerRecord.signerNo || this.mySignerRecord.signerId,
      layerNo: this.mySignerRecord.layerNo,
      fieldId: this.mySignerRecord.fieldId,
      orgId: parseInt(this.message.getOrgId().toString()!),
      userId: parseInt(this.message.getUserId().toString()!),
      action: 'Decline',
      comments: this.declineComments,
      signedName: '',
      signFont: ''
    };

    this.appService.performEsignAction(payload).subscribe({
      next: (res: any) => {
        this.isProcessing = false;
        this.showSnackBar(res.result || 'Declined.', res.resultType === 'S' ? 'S' : 'E');
        if (res.resultType === 'S') {
          setTimeout(() => this.router.navigate(['/dashboard/home']), 1500);
        }
      },
      error: () => {
        this.isProcessing = false;
        this.showSnackBar('Server error. Please retry.', 'E');
      }
    });
  }

  prevPage() { if (this.currentPage > 1) this.renderPage(this.currentPage - 1); }
  nextPage() { if (this.currentPage < this.totalPages) this.renderPage(this.currentPage + 1); }

  getPdfCursor(): string {
    return (this.mySignerRecord && this.isPendingStatus(this.mySignerRecord.status))
      ? 'pointer' : 'default';
  }

  showSnackBar(message: string, type: string) {
    const sb = this.snackBar.openFromComponent(SuccessSnackbarComponent, {
      duration: 5000,
      data: { message, type: type === 'S' ? 'Success' : 'Error', buttonLabel: 'Ok' },
      horizontalPosition: 'center', verticalPosition: 'top',
      panelClass: type === 'S' ? 'Success' : 'Error'
    });
    sb.onAction().subscribe(() => sb.dismiss());
  }
}
